import type { ComputedRef, Ref } from 'vue'
import type { PromoRewardDto } from '~/types/api/promo'
import { useDepositCatalog } from '~/composables/useDepositCatalog'
import { parseMoney } from '~/utils/transfer/money'
import type { PromoActivity } from '../types'

/** 详情表行：左标签右值 */
export interface PromoDetailRow {
  label: string
  value: string
}

/** 详情视图模型：banner 高亮 + 详情表行 + 细则 bullet */
export interface PromoDetailInfo {
  /** banner 上的「奖励 最高 X」；无奖励概念时为空 */
  rewardHighlight: string
  rows: PromoDetailRow[]
  rules: string[]
}

const BET_METRICS = ['BET_AMOUNT', 'WIN_AMOUNT', 'MULTIPLIER']
/** 产品给定细则句式的模板：只显示「参与对象 + 固定说明」，不再追加模板展开句 */
const COMPACT_RULE_TEMPLATES = [
  'deposit_seq',
  'first_withdraw',
  'registration_milestone',
  'channel_first_deposit',
  'daily_deposit',
  'date_deposit',
  'bet_extreme',
  'loss_rescue',
  'deposit_streak',
  'birthday',
  'comeback',
]
const DEPOSIT_VALIDITY_TEMPLATES = [
  'deposit_seq',
  'channel_first_deposit',
  'daily_deposit',
  'date_deposit',
  'comeback',
]

/** 金额串直接拼接币种；后端给的是展示态十进制字符串，不二次加工 */
function fmtAmtRaw(amount: string | undefined, currency?: string): string {
  const a = (amount ?? '').trim()
  return a ? `${a} ${currency ?? ''}`.trim() : ''
}

/** 比例 "0.3" → "30%"；去尾零 */
function fmtRate(rate: string | undefined): string {
  if (!rate) return ''
  const pct = parseMoney(rate) * 100
  if (!Number.isFinite(pct)) return ''
  return `${Number(pct.toFixed(4))}%`
}

/** 十进制字符串比大小取最大展示值 */
function maxAmt(list: (string | undefined)[], currency?: string): string {
  let best = ''
  let bestNum = -1
  for (const raw of list) {
    const s = (raw ?? '').trim()
    if (!s) continue
    const n = parseMoney(s)
    if (Number.isFinite(n) && n > bestNum) {
      bestNum = n
      best = s
    }
  }
  return best ? fmtAmtRaw(best, currency) : ''
}

/** 奖励的最大金额：RATE 取封顶、TIERED / PERIOD_TRACKS 取最大档、VIP_TABLE 取区间上限 */
function topAmount(rw?: PromoRewardDto): string {
  if (!rw) return ''
  const cur = rw.currency
  switch (rw.kind) {
    case 'RATE':
      return fmtAmtRaw(rw.amount_cap, cur)
    case 'TIERED':
      return maxAmt(
        (rw.tiers ?? []).flatMap(x => [x.amount, x.random_max]),
        cur,
      )
    case 'PERIOD_TRACKS':
      return maxAmt(
        (rw.period_tracks ?? []).map(x => x.amount),
        cur,
      )
    case 'VIP_TABLE':
      return fmtAmtRaw(rw.amount_max, cur)
    default:
      return ''
  }
}

/**
 * 活动详情结构化内容：按 template_code 选模板口径，
 * 所有字段缺席即省略，绝不补 0；业务固定说明由前端生成（交接文档 §二/§四）。
 */
export function usePromoDetailInfo(activity: Ref<PromoActivity | null>) {
  const { t } = useI18n()
  const { data: catalog } = useDepositCatalog()

  const D = 'promo.acts.detail'
  const K = `${D}.rule`

  /** type_key 拆键（逗号/顿号/空白分隔） */
  const channelKeys = (key?: string): string[] =>
    key ? key.split(/[，、\s,]+/).filter(Boolean) : []

  /** 键名解析：先按类型 type_key，再按渠道 channel_code，均未命中回退原始键 */
  const channelNameOf = (key?: string): string => {
    if (!key) return ''
    const tps = catalog.value?.types ?? []
    const ch = tps
      .flatMap(x => x.channels ?? [])
      .find(c => c.channel_code?.toLowerCase() === key.toLowerCase())
    return tps.find(x => x.type_key === key)?.name || ch?.name || key
  }

  /**
   * 充值范围行（rule.deposit_eligibility 优先，接口按当前玩家可见渠道下发）：
   * ewallet → 钱包类型（wallet_types），其他 → 币种范围（currencies），互斥。
   * 仅 channel_first_deposit 使用；date_deposit 不展示充值范围行。
   * 仅展示用途，不改变领取规则；字段缺席返回 null。
   */
  const eligibilityRow = (a: PromoActivity): PromoDetailRow | null => {
    const el = a.rule?.deposit_eligibility
    if (!el) return null
    if (el.wallet_types?.length)
      return { label: t(`${D}.rowChannel`), value: el.wallet_types.join(sep.value) }
    if (el.currencies?.length)
      return { label: t(`${D}.rowCurrency`), value: el.currencies.join(sep.value) }
    return null
  }

  /** 渠道明细行：单 type_key 列该类型下渠道（币种等）；多键列各键名称（钱包） */
  const channelRow = (a: PromoActivity): PromoDetailRow | null => {
    const keys = channelKeys(a.rule?.type_key)
    if (!keys.length) return null
    if (keys.length > 1)
      return {
        label: t(`${D}.rowChannel`),
        value: t(`${D}.channelTypesEtc`, { types: keys.map(channelNameOf).join(sep.value) }),
      }
    const tp = catalog.value?.types.find(x => x.type_key === keys[0])
    const names = tp
      ? (tp.channels ?? []).map(c => c.name || c.currency || '').filter(Boolean)
      : [channelNameOf(keys[0])]
    if (!names.length) return null
    return {
      label: t(keys[0] === 'crypto' ? `${D}.rowCurrency` : `${D}.rowChannel`),
      value: t(`${D}.channelTypesEtc`, { types: names.join(sep.value) }),
    }
  }

  const sep = computed(() => t(`${D}.listSep`))
  const clauseSep = computed(() => t(`${D}.clauseSep`))

  /** 展示币种：CNY 按站点习惯显示为「元」（en 仍显示 CNY），其余币种原样 */
  const curLabel = (c?: string) => (c?.toUpperCase() === 'CNY' ? t(`${D}.unitYuan`) : c)

  /** 金额 + 展示币种（覆盖模块级 fmtAmtRaw，统一走 curLabel） */
  const fmtAmt = (amount?: string, currency?: string) => fmtAmtRaw(amount, curLabel(currency))

  /** 最大展示金额，币种先过 curLabel */
  const topAmt = (rw?: PromoRewardDto) =>
    rw ? topAmount({ ...rw, currency: curLabel(rw.currency) }) : ''

  /** 单笔门槛子句：「，单笔金额不低于 M，」；未提供不展示 */
  const reqAmt = (amount: string | undefined, currency?: string, key = 'reqAmount') =>
    amount ? t(`${D}.${key}`, { amount: fmtAmt(amount, currency) }) : ''

  /** date_deposit 的活动日子句：「每周一、周五」/「每月 1 日、15 日」 */
  const dateDaysText = (a: PromoActivity): string => {
    const s = a.schedule
    if (!s) return ''
    if (s.cycle_type === 'WEEKLY' && s.fixed_weekdays?.length) {
      const days = s.fixed_weekdays.map(d => t(`${D}.weekday.${d}`)).join(sep.value)
      return t(`${D}.weeklyPrefix`, { days })
    }
    if (s.cycle_type === 'MONTHLY' && s.fixed_dates?.length) {
      const days = s.fixed_dates.map(d => t(`${D}.dayOfMonth`, { d })).join(sep.value)
      return t(`${D}.monthlyPrefix`, { days })
    }
    return ''
  }

  /** 比例奖励的计算基数名称 */
  const rewardBase = (a: PromoActivity): string => {
    const r = a.rule ?? {}
    switch (a.templateCode) {
      case 'first_withdraw':
        return t(`${D}.baseWithdraw`)
      case 'loss_rescue':
        return t(`${D}.baseLoss`)
      case 'comeback':
        return t(`${D}.baseDeposit`)
      case 'bet_extreme':
        return t(`${D}.${r.metric === 'BET_AMOUNT' ? 'baseTicket' : 'baseWin'}`)
      default:
        return t(`${D}.baseDeposit`)
    }
  }

  /** 触发时机行 */
  const triggerText = (a: PromoActivity): string => {
    const r = a.rule ?? {}
    switch (a.templateCode) {
      case 'deposit_seq': {
        const n = Number(r.ordinal)
        return n >= 1 ? t(`${K}.trigDepositSeq`, { n }) : ''
      }
      case 'first_withdraw':
        return t(`${K}.trigFirstWithdraw`)
      case 'registration_milestone':
        return r.milestone_days ? t(`${K}.trigRegMilestone`, { days: r.milestone_days }) : ''
      case 'channel_first_deposit': {
        const keys = channelKeys(r.type_key)
        return keys.length
          ? t(`${K}.trigChannelNamed`, { type: keys.map(channelNameOf).join(sep.value) })
          : t(`${K}.trigChannelFirst`)
      }
      case 'daily_deposit':
        return t(`${K}.trigDaily`)
      case 'date_deposit': {
        const days = dateDaysText(a)
        return days ? t(`${K}.trigDateOn`, { days }) : t(`${K}.trigDate`)
      }
      case 'bet_extreme':
        return t(`${K}.trigBetExtreme`)
      case 'loss_rescue':
        return t(`${K}.trigLossRescue`)
      case 'deposit_streak':
        return t(`${K}.trigStreak`)
      case 'birthday':
        return t(`${K}.trigBirthday`)
      case 'comeback':
        return t(`${K}.trigComeback`)
      default:
        return ''
    }
  }

  /** 模板条件行：首行 = 资格要求句，其余 = 细则 bullet（交接文档 §二 逐模板口径） */
  const ruleLines = (a: PromoActivity): string[] => {
    const r = a.rule ?? {}
    const cur = a.reward?.currency
    const isRate = a.reward?.kind === 'RATE'
    switch (a.templateCode) {
      case 'deposit_seq': {
        const n = Number(r.ordinal)
        const min = r.minimum_amount
          ? t(`${K}.reqMinDeposit`, { amount: fmtAmt(r.minimum_amount, cur) })
          : ''
        const requirement =
          n === 1
            ? t(`${K}.reqDepositFirst`)
            : n === 2
              ? t(`${K}.reqDepositSecond`)
              : n === 3
                ? t(`${K}.reqDepositThird`)
                : n >= 4
                  ? t(`${K}.reqDepositNth`, { c: n - 1 })
                  : ''
        const lines = [
          requirement ? `${requirement}${min}` : min.replace(/^，/, ''),
          ...(n >= 1 ? [t(`${K}.depositSeq`, { n, req: reqAmt(r.minimum_amount, cur) })] : []),
          t(`${K}.depositSeqNote`),
        ]
        if (isRate && n >= 1) lines.push(t(`${K}.depositSeqBase`, { n }))
        return lines
      }
      case 'first_withdraw': {
        const within = r.maximum_registration_days
          ? t(`${K}.withinDays`, { days: r.maximum_registration_days })
          : ''
        const min = r.minimum_amount
          ? t(`${K}.reqMinWithdraw`, { amount: fmtAmt(r.minimum_amount, cur) })
          : ''
        const requirement = r.maximum_registration_days
          ? t(`${K}.reqFirstWithdraw`, { days: r.maximum_registration_days })
          : t(`${K}.reqFirstWithdrawAny`)
        const lines = [
          `${requirement}${min}`,
          t(`${K}.firstWithdraw`, { within, req: reqAmt(r.minimum_amount, cur, 'reqWithdraw') }),
          t(`${K}.firstWithdrawNote`),
        ]
        if (isRate) lines.push(t(`${K}.firstWithdrawBase`))
        return lines
      }
      case 'registration_milestone': {
        const hasCum = !!r.cumulative_deposit && parseMoney(r.cumulative_deposit) > 0
        const cumAmt = hasCum ? fmtAmt(r.cumulative_deposit, cur) : ''
        const days = r.milestone_days ?? 0
        const lines = [
          `${t(`${K}.reqRegMilestone`, { days })}${hasCum ? t(`${K}.reqRegCum`, { amount: cumAmt }) : ''}`,
          t(`${K}.regMilestone`, {
            days,
            cum: hasCum ? t(`${K}.andCumDeposit`, { amount: cumAmt }) : '',
          }),
        ]
        if (a.reward?.kind === 'TIERED') lines.push(t(`${K}.regMilestoneTier`))
        return lines
      }
      case 'channel_first_deposit': {
        const keys = channelKeys(r.type_key)
        const min = r.minimum_amount
          ? t(`${K}.reqMinDeposit`, { amount: fmtAmt(r.minimum_amount, cur) })
          : ''
        const req = keys.length
          ? t(`${K}.reqChannelNamed`, { type: keys.map(channelNameOf).join(sep.value) })
          : t(`${K}.reqChannelFirst`)
        return [
          `${req}${min}`,
          t(`${K}.channelFirst`, {
            type: keys.map(channelNameOf).join(sep.value),
            req: reqAmt(r.minimum_amount, cur),
          }),
          t(`${K}.channelFirstNote`),
        ]
      }
      case 'daily_deposit':
        return [t(`${K}.reqDailyDeposit`)]
      case 'date_deposit':
        return []
      case 'bet_extreme':
        return []
      case 'loss_rescue': {
        const sn = scopeShortNames(a)
        const amt = fmtAmt(r.net_loss_threshold, cur)
        return [
          sn
            ? amt
              ? t(`${K}.reqLossRescueScoped`, { scope: sn, amount: amt })
              : t(`${K}.reqLossRescueScopedAny`, { scope: sn })
            : amt
              ? t(`${K}.reqLossRescue`, { amount: amt })
              : t(`${K}.reqLossRescueAny`),
        ]
      }
      case 'deposit_streak':
        return []
      case 'birthday':
        return [
          r.minimum_vip_level
            ? t(`${K}.reqBirthdayVip`, { level: r.minimum_vip_level })
            : t(`${K}.reqBirthdayAny`),
        ]
      case 'comeback': {
        const conds: string[] = []
        if (r.lifetime_deposit_minimum && parseMoney(r.lifetime_deposit_minimum) > 0)
          conds.push(t(`${K}.reqComebackAmt`, { amount: fmtAmt(r.lifetime_deposit_minimum, cur) }))
        if (r.lifetime_deposit_count_minimum)
          conds.push(t(`${K}.reqComebackCount`, { n: r.lifetime_deposit_count_minimum }))
        if (r.deposit_silence_days)
          conds.push(t(`${K}.reqComebackNoDep`, { days: r.deposit_silence_days }))
        if (r.game_silence_days)
          conds.push(t(`${K}.reqComebackNoGame`, { days: r.game_silence_days }))
        return [conds.length ? conds.join(sep.value) : t(`${K}.reqComebackAny`)]
      }
      default:
        return []
    }
  }

  /** 奖励方式行 */
  const rewardMethod = (a: PromoActivity): string => {
    const rw = a.reward
    switch (rw?.kind) {
      case 'FIXED':
        return t(`${D}.methodFixed`)
      case 'RATE': {
        const rate = fmtRate(rw.rate)
        if (!rate) return ''
        return t(`${D}.methodRatePlain`, { base: rewardBase(a), rate })
      }
      case 'TIERED':
        return t(`${D}.methodTiered`)
      case 'PERIOD_TRACKS':
        return t(`${D}.methodTracks`)
      case 'VIP_TABLE':
        return t(`${D}.methodVip`)
      default:
        return ''
    }
  }

  /** 奖励金额行：FIXED 定额；VIP_TABLE 有区间时展示区间 */
  const rewardAmountText = (rw?: PromoRewardDto): string => {
    if (!rw) return ''
    if (rw.kind === 'FIXED') return fmtAmt(rw.amount, rw.currency)
    if (rw.kind === 'VIP_TABLE' && rw.amount_min && rw.amount_max) {
      return t(`${D}.vipRange`, {
        min: fmtAmt(rw.amount_min, rw.currency),
        max: fmtAmt(rw.amount_max, rw.currency),
      })
    }
    return ''
  }

  /** 奖励上限行：仅在存在真实封顶 / 最大档时展示；每日首充封顶按天计 */
  const rewardCapText = (a: PromoActivity): string => {
    const rw = a.reward
    if (!rw || !['RATE', 'TIERED', 'PERIOD_TRACKS'].includes(rw.kind ?? '')) return ''
    const top = topAmt(rw)
    if (!top) return ''
    return t(`${D}.${a.templateCode === 'daily_deposit' ? 'capTopDaily' : 'capTop'}`, {
      amount: top,
    })
  }

  /** banner 高亮：定额「奖励 X」，其余「奖励 最高 X」 */
  const highlightText = (rw?: PromoRewardDto): string => {
    if (!rw) return ''
    if (rw.kind === 'FIXED') {
      const amt = fmtAmt(rw.amount, rw.currency)
      return amt ? t(`${D}.hlReward`, { amount: amt }) : ''
    }
    const top = topAmt(rw)
    return top ? t(`${D}.hlRewardTop`, { amount: top }) : ''
  }

  /** 奖励相关细则：分档明细、VIP 说明、余额去向 */
  const rewardBullets = (rw?: PromoRewardDto, withTarget = true): string[] => {
    const lines: string[] = []
    if (!rw) return lines
    const cur = rw.currency
    if (rw.kind === 'TIERED') {
      for (const tier of rw.tiers ?? []) {
        const value = tier.amount
          ? fmtAmt(tier.amount, cur)
          : tier.random_min && tier.random_max
            ? t(`${D}.tierRandom`, {
                min: fmtAmt(tier.random_min, cur),
                max: fmtAmt(tier.random_max, cur),
              })
            : ''
        const threshold = fmtAmt(tier.threshold, cur)
        if (value && threshold) lines.push(t(`${D}.tierRow`, { threshold, value }))
      }
    }
    if (rw.kind === 'VIP_TABLE') lines.push(t(`${D}.rewardVipTable`))
    if (withTarget && rw.target === 'bonus') lines.push(t(`${D}.targetBonus`))
    else if (withTarget && rw.target === 'available') lines.push(t(`${D}.targetAvailable`))
    return lines
  }

  /** 领取时效行：生日固定 24 小时；其余按 window_days，空 / 0 不展示 */
  const validityText = (a: PromoActivity): string => {
    if (a.templateCode === 'birthday') return t(`${D}.validityBirthday`, { h: 24 })
    const days = a.claimPolicy?.window_days
    if (!days || days <= 0) return ''
    const key = DEPOSIT_VALIDITY_TEMPLATES.includes(a.templateCode)
      ? 'validityDeposit'
      : a.templateCode === 'first_withdraw'
        ? 'validityWithdraw'
        : 'validityDays'
    return t(`${D}.${key}`, { d: days })
  }

  /** 领取次数行：多条限制以「；」连接 */
  const claimCountText = (a: PromoActivity): string => {
    const p = a.claimPolicy
    if (a.templateCode === 'deposit_streak') {
      const periods = (a.rule?.tracks ?? []).map(x => x.period)
      const w = periods.includes('WEEKLY')
      const m = periods.includes('MONTHLY')
      return w && m
        ? t(`${D}.limitStreakBoth`)
        : w
          ? t(`${D}.limitStreakWeekly`)
          : m
            ? t(`${D}.limitStreakMonthly`)
            : ''
    }
    const parts: string[] = []
    if (p?.lifetime_limit != null)
      parts.push(
        t(
          a.templateCode === 'channel_first_deposit' && channelKeys(a.rule?.type_key).length > 1
            ? `${D}.limitPerWallet`
            : `${D}.limitLifetime`,
          { n: p.lifetime_limit },
        ),
      )
    if (p?.daily_limit != null) {
      const key =
        a.templateCode !== 'date_deposit'
          ? 'limitDaily'
          : a.rule?.type_key
            ? 'limitDailyPerDay'
            : 'limitMemberDay'
      parts.push(t(`${D}.${key}`, { n: p.daily_limit }))
    }
    if (p?.yearly_limit != null) parts.push(t(`${D}.limitYearly`, { n: p.yearly_limit }))
    return parts.join(clauseSep.value)
  }

  /** scope 名称列表（vendor + 分类 + 游戏） */
  const scopeNameList = (a: PromoActivity): string[] => {
    const s = a.scope
    const names = (list?: { name?: string }[]) =>
      (list ?? []).map(x => x.name ?? '').filter(Boolean)
    return [...names(s?.vendors), ...names(s?.game_categories), ...names(s?.games)]
  }

  /** scope 名称拼接 */
  const scopeNames = (a: PromoActivity): string => scopeNameList(a).join(sep.value)

  /** scope 短名拼接：剥掉「游戏/视讯」后缀，用于资格句 */
  const scopeShortNames = (a: PromoActivity): string =>
    scopeNameList(a)
      .map(n => n.replace(/（游戏|视讯）$/, ''))
      .join(sep.value)

  /** bet_extreme 游戏范围行：列出已配置范围并追加「可配置指定游戏」 */
  const gameScopeText = (a: PromoActivity): string => {
    const all = scopeNames(a)
    return `${all || t(`${D}.scopeAll`)}${t(`${D}.scopeConfigurable`)}`
  }

  const info: ComputedRef<PromoDetailInfo> = computed(() => {
    const a = activity.value
    if (!a) return { rewardHighlight: '', rows: [], rules: [] }

    const isDisplay = a.templateCode === 'display'
    const ruleAll = ruleLines(a)
    // deposit_streak / display 无单行资格句，全部为细则
    const hasReqRow = !['deposit_streak', 'display'].includes(a.templateCode)
    const requirement = hasReqRow ? (ruleAll[0] ?? '') : ''
    const ruleBullets = hasReqRow ? ruleAll.slice(1) : ruleAll

    const rows: PromoDetailRow[] = []
    const push = (labelKey: string, value: string) => {
      if (value) rows.push({ label: t(`${D}.${labelKey}`), value })
    }

    push('rowDesc', a.description.trim())
    if (!isDisplay) {
      if (a.templateCode === 'date_deposit') {
        push('rowSchedule', dateDaysText(a))
      } else if (a.templateCode === 'deposit_streak') {
        const trackCond = (period: string): string => {
          const tr = a.rule?.tracks?.find(x => x.period === period)
          if (!tr) return ''
          const days = Number(tr.required_days ?? 0)
          const base = days > 0 ? t(`${D}.streakCondDays`, { n: days }) : t(`${D}.streakCondAny`)
          const min = tr.daily_minimum_amount
            ? t(`${D}.streakCondGe`, {
                amount: fmtAmt(tr.daily_minimum_amount, a.reward?.currency),
              })
            : ''
          return `${base}${min}`
        }
        const wc = trackCond('WEEKLY')
        const mc = trackCond('MONTHLY')
        push('rowStreakWeek', wc ? t(`${D}.streakWeekRow`, { cond: wc }) : '')
        push('rowStreakMonth', mc ? t(`${D}.streakMonthRow`, { cond: mc }) : '')
      } else if (a.templateCode === 'loss_rescue') {
        push('rowTrigger', triggerText(a))
        const sn = scopeNames(a)
        push('rowScope', sn ? t(`${D}.scopeAllCat`, { name: sn }) : '')
        push('rowRequire', requirement)
      } else if (a.templateCode === 'bet_extreme') {
        push('rowTrigger', triggerText(a))
        const m = a.rule?.metric
        push(
          'rowCalc',
          m && BET_METRICS.includes(m)
            ? t(`${K}.calcBetExtreme`, { metric: t(`${K}.metricShort.${m}`) })
            : '',
        )
        push('rowScope', gameScopeText(a))
        const th = a.rule?.threshold
        if (th) {
          const metricName = t(
            `${D}.${m === 'WIN_AMOUNT' ? 'baseWin' : m === 'MULTIPLIER' ? 'baseMultiplier' : 'baseTicket'}`,
          )
          push(
            'rowThreshold',
            m === 'MULTIPLIER'
              ? t(`${D}.thresholdMult`, { metric: metricName, v: th })
              : t(`${D}.thresholdAmtGe`, {
                  metric: metricName,
                  amount: fmtAmt(th, a.reward?.currency),
                }),
          )
        }
      } else {
        push(
          a.templateCode === 'channel_first_deposit' ? 'rowDepositReq' : 'rowTrigger',
          triggerText(a),
        )
        push('rowRequire', requirement)
      }
      if (a.templateCode === 'channel_first_deposit') {
        const row = eligibilityRow(a) ?? channelRow(a)
        if (row) rows.push(row)
      } else {
        const keys = channelKeys(a.rule?.type_key)
        if (keys.length)
          rows.push({
            label: t(`${D}.rowChannel`),
            value: keys.map(channelNameOf).join(sep.value),
          })
      }
      if (['daily_deposit', 'date_deposit'].includes(a.templateCode)) {
        const minAmt = fmtAmt(a.rule?.minimum_amount, a.reward?.currency)
        const tk = a.templateCode === 'date_deposit' ? a.rule?.type_key : undefined
        const typeName = channelKeys(tk).map(channelNameOf).join(sep.value)
        const reqKey = typeName ? (minAmt ? 'reqChannelTypeMin' : 'reqChannelType') : 'minDeposit'
        push(
          'rowDepositReq',
          typeName || minAmt ? t(`${D}.${reqKey}`, { type: typeName, amount: minAmt }) : '',
        )
      }
      push('rowMethod', rewardMethod(a))
      push('rowAmount', a.templateCode === 'birthday' ? '' : rewardAmountText(a.reward))
      if (a.templateCode === 'birthday') {
        const rw = a.reward
        const mn = fmtAmt(rw?.amount_min, rw?.currency)
        const mx = fmtAmt(rw?.amount_max, rw?.currency)
        push('rowRewardRange', mn && mx ? t(`${D}.amountRange`, { min: mn, max: mx }) : mn || mx)
      }
      if (a.templateCode === 'deposit_streak') {
        const pt = (p: string) => a.reward?.period_tracks?.find(x => x.period === p)?.amount
        const wAmt = pt('WEEKLY')
        const mAmt = pt('MONTHLY')
        push(
          'rowStreakWeekPrize',
          wAmt ? t(`${D}.capTop`, { amount: fmtAmt(wAmt, a.reward?.currency) }) : '',
        )
        push(
          'rowStreakMonthPrize',
          mAmt ? t(`${D}.capTop`, { amount: fmtAmt(mAmt, a.reward?.currency) }) : '',
        )
      }
      push(
        'rowCap',
        ['deposit_streak', 'birthday'].includes(a.templateCode) ? '' : rewardCapText(a),
      )
      push('rowClaimWay', t(a.claimMode === 'AUTO' ? `${D}.claimAuto` : `${D}.claimManual`))
      push('rowValidity', validityText(a))
      push('rowCount', claimCountText(a))
      push(
        'rowWager',
        a.reward?.wagering_multiple ? t(`${D}.wagering`, { n: a.reward.wagering_multiple }) : '',
      )
      push('rowNote', a.templateCode === 'birthday' ? t(`${D}.noteBirthday`) : '')
    }

    const validity = validityText(a)
    const compact = COMPACT_RULE_TEMPLATES.includes(a.templateCode)
    /** 细则优先取接口 terms；缺席时按模板字段生成（交接文档口径） */
    const rules = a.terms.length
      ? a.terms.filter(Boolean)
      : isDisplay
        ? [t(`${D}.ruleDisclaimer`)]
        : [
            ...(requirement ? [t(`${D}.ruleTarget`, { v: requirement })] : []),
            ...(compact ? [] : ruleBullets),
            ...rewardBullets(a.reward, !compact),
            t(a.claimMode === 'AUTO' ? `${D}.ruleClaimAuto` : `${D}.ruleClaimManual`),
            ...(validity ? [t(`${D}.ruleValidity`, { v: validity })] : []),
            ...(a.reward?.wagering_multiple
              ? [t(`${D}.ruleWager`, { n: a.reward.wagering_multiple })]
              : []),
            ...(a.templateCode === 'birthday' ? [t(`${K}.birthdayNote`)] : []),
            t(`${D}.ruleDisclaimer`),
          ]

    return { rewardHighlight: highlightText(a.reward), rows, rules }
  })

  return { info }
}
